import { AppWindow, Loader2, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { adminClient, type IntegrationDraft, type IntegrationSourceApp } from '@/api/adminClient';
import { Button } from '@/components/ui/button';

import { errorMessage } from './builderModel';

/** Whether its owners let it be listed for other organizations, in words. */
export function appListingText(app: IntegrationSourceApp | undefined): string {
  if (!app) return 'Loading the app it was made from…';
  if (app.deleted) return 'Its owners deleted this integration in Manage → API. It cannot be published or released.';
  return app.offeredToEveryone
    ? 'Its owners offer it to everyone. Publishing it lists it for the RapidSOS team, and releasing it lists it for every organization.'
    : 'Its owners keep it in their own organization, where apps already use it. It can be published or released only after they choose Everyone in Manage → API.';
}

/**
 * The Studio app an integration was made from (Manage → API → Create
 * integration): its endpoints and how it is called stay with the app, so the
 * builder edits only the listing. "Write with AI" redoes the copy, keywords
 * and translations from the contract, and draws a logo when there is none.
 */
export function AppSourcePanel({
  draft,
  app,
  editable,
  replace,
}: {
  draft: IntegrationDraft;
  app: IntegrationSourceApp | undefined;
  editable: boolean;
  replace: (draft: IntegrationDraft) => void;
}) {
  const [writing, setWriting] = useState(false);

  const write = async () => {
    setWriting(true);
    try {
      const next = await adminClient.writeIntegrationDraftCopy(draft.draftId);
      replace(next);
      toast.success(`Written with AI: ${next.generation.filled.join(', ')}`);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setWriting(false);
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-border bg-card/40 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <AppWindow className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
          <div className="min-w-0 space-y-1">
            <div className="font-medium">
              Made from the Studio app {app?.appName ? <span className="font-semibold">{app.appName}</span> : 'API'}
              {app ? <span className="ml-1 font-mono text-[11px] text-muted-foreground">{app.organizationId}</span> : null}
            </div>
            <p className="text-xs text-muted-foreground">
              Its endpoints come from the app's published version, and calls go through Studio to the app. The app's owners choose the endpoints in
              Manage → API, so the contract, pricing and connection are read-only here. Edit the copy, logo and keywords.
            </p>
            <p className="text-xs text-muted-foreground">{appListingText(app)}</p>
          </div>
        </div>
        {editable ? (
          <Button variant="outline" size="sm" disabled={writing} onClick={() => void write()}>
            {writing ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-2 h-3.5 w-3.5" />}
            Write with AI
          </Button>
        ) : null}
      </div>
    </div>
  );
}
